import React, { useCallback, useEffect, useReducer, useContext } from 'react'
import moment from 'moment'
import qs from 'qs'
import GeoContext from 'contexts/geoContext'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import { resetFlights } from 'actions/CartActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import flightSearchWidgetStateReducer, { initialState } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import { FlightSearchWidgetState } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { FlightsFareTypes } from 'constants/flight'
import { FlightsCreditDetails } from 'components/Flights/types'
import { isFlightsCredit } from 'selectors/flightsSelectors'
import { getPopularAirports } from 'actions/FlightActions'

import FlightSearchWidgetDesktop from './FlightSearchWidgetDesktop'
import FlightSearchWidgetMobile from './FlightSearchWidgetMobile'
import { countOccupantsForFlights } from 'lib/occupancyUtils'
import GlobalSearchTrackingContext from 'contexts/GlobalSearch/GlobalSearchTracking'
import { OFFER_TYPE_FLIGHT } from 'constants/offer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { fireInteractionEvent } from 'api/googleTagManager'
import { flightsFlightSearchClick } from 'analytics/eventDefinitions'
import * as Analytics from 'analytics/analytics'
import { searchFromHomepageEvent } from 'analytics/optimizely/events'

export enum StandaloneFlightMenu {
  departAirport = 'departAirport',
  arrivalAirport = 'arrivalAirport',
  date = 'date',
  occupants = 'occupants'
}

interface Props {
  className?: string;
  searchData?: FlightSearchWidgetState;
  creditDetails?: FlightsCreditDetails;
  isFlightsCredit: boolean;
  pathname: string;
  regionCode: string;
  onSubmit?: () => void;
}

function FlightSearchWidget(props: Props) {
  const dispatch = useAppDispatch()
  const { searchData, isFlightsCredit, creditDetails, onSubmit, className, pathname, regionCode } = props
  const { currentRegionCode } = useContext(GeoContext)

  const [state, stateDispatch] = useReducer(flightSearchWidgetStateReducer, {
    ...initialState,
    ...searchData,
  })

  useEffect(() => {
    if (searchData) {
      stateDispatch({
        type: FlightSearchWidgetActions.UPDATE_FLIGHT_SEARCH_WIDGET_DATA,
        payload: searchData,
      })
    }
  }, [searchData])

  const {
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    occupants,
    fareType,
    fareCabin,
  } = state

  const onSearchEvent = useContext(GlobalSearchTrackingContext)

  const onSearchAirport = useCallback(() => {
    onSubmit?.()

    const transformedOccupants = countOccupantsForFlights([occupants])
    const isReturnFare = fareType.value === FlightsFareTypes.RETURN

    const queryParams = {
      originAirportCode: departureAirport.airportCode,
      originAirportName: departureAirport.airportName,
      destinationAirportCode: arrivalAirport.airportCode,
      destinationAirportName: arrivalAirport.airportName,
      departDate: moment(checkinDate).format(ISO_DATE_FORMAT),
      returnDate: isReturnFare ? moment(checkoutDate).format(ISO_DATE_FORMAT) : null,
      adults: transformedOccupants.adults,
      children: transformedOccupants.children,
      infants: transformedOccupants.infants,
      childrenAge: transformedOccupants.childrenAge.join(','),
      fareClass: fareCabin.value,
      fareType: fareType.value,
      searchId: moment().unix(),
      carrier: isFlightsCredit ? creditDetails?.carrier : undefined,
      flightsCredit: isFlightsCredit || undefined,
    }

    if (onSearchEvent) {
      const params = {
        vertical: OFFER_TYPE_FLIGHT,
      }
      onSearchEvent('search-button', params)
    }

    if (pathname === `/${regionCode.toLowerCase()}`) {
      Analytics.track('search-from-homepage', searchFromHomepageEvent())
    }

    fireInteractionEvent(flightsFlightSearchClick())
    dispatch(resetFlights())
    dispatch(pushWithRegion('/flights-search-results', qs.stringify(queryParams)))
  }, [onSubmit, departureAirport, arrivalAirport, occupants, fareType.value, checkinDate, checkoutDate, fareCabin.value, isFlightsCredit, creditDetails?.carrier, dispatch, onSearchEvent, pathname, regionCode])

  useEffect(() => {
    dispatch(getPopularAirports())
  }, [dispatch, currentRegionCode])

  return (
    <FlightSearchWidgetStateProvider state={state} dispatch={stateDispatch}>
      <div className={className}>
        <CSSBreakpoint min="sm">
          <FlightSearchWidgetDesktop onSearchAirport={onSearchAirport} />
        </CSSBreakpoint>
        <CSSBreakpoint max="xs">
          <FlightSearchWidgetMobile onSearchAirport={onSearchAirport} />
        </CSSBreakpoint>
      </div>
    </FlightSearchWidgetStateProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    creditDetails: state.flights.creditDetails,
    isFlightsCredit: isFlightsCredit(state),
    pathname: state.router.location.pathname,
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(FlightSearchWidget)
